import React, { useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ReactFlow,
  Background,
  Controls,
  MarkerType,
  Handle,
  Position,
  useNodesState,
  useEdgesState
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { 
  FiActivity, 
  FiMonitor,
  FiTarget,
  FiPackage,
  FiUsers
} from 'react-icons/fi';
import logoImg from '../../../images/Logo/logoESM.png';
import './EsmInteractionFlowPage.scss';

// Custom Node Component to allow connecting from any side
const EsmCustomNode = ({ data, id }) => {
  return (
    <div className={`esm-node-wrapper ${data.variant === 'plain' ? 'is-plain' : 'is-hexagon'}`}>
      <Handle type="target" position={Position.Top} id="top" style={{ opacity: 0 }} />
      <Handle type="source" position={Position.Top} id="top-s" style={{ opacity: 0 }} />
      <Handle type="target" position={Position.Right} id="right" style={{ opacity: 0 }} />
      <Handle type="source" position={Position.Right} id="right-s" style={{ opacity: 0 }} />
      <Handle type="target" position={Position.Bottom} id="bottom" style={{ opacity: 0 }} />
      <Handle type="source" position={Position.Bottom} id="bottom-s" style={{ opacity: 0 }} />
      <Handle type="target" position={Position.Left} id="left" style={{ opacity: 0 }} />
      <Handle type="source" position={Position.Left} id="left-s" style={{ opacity: 0 }} />
      
      {data.variant === 'hexagon' && (
        <div className="hexagon-icon">
          {data.icon}
        </div>
      )}

      {data.variant === 'plain' && data.isLogo && (
        <div className="plain-icon logo-icon">
          <img src={logoImg} alt="ESM Logo" />
        </div>
      )}

      {data.variant === 'plain' && !data.isLogo && (
        <div className="plain-icon">
          {data.icon}
        </div>
      )}

      <div className="node-label" dangerouslySetInnerHTML={{ __html: data.label }}></div>
    </div>
  );
};

const nodeTypes = {
  custom: EsmCustomNode,
};

const EsmInteractionFlowPage = () => {
  const { i18n } = useTranslation();
  const isEnglish = i18n.language.startsWith('en');

  const initialNodes = useMemo(() => {
    return [
      // 1. Khối Công Nghệ Y Tế (ESM) - Left
      {
        id: 'node-cnyt',
        type: 'custom',
        position: { x: 50, y: 400 },
        data: { variant: 'plain', isLogo: true, label: isEnglish ? 'Medical<br/>Technology Division' : 'Khối Công<br/>Nghệ Y Tế' },
        draggable: false,
      },
      // 2. Bệnh Nhân - Top Left (above Công Nghệ)
      {
        id: 'node-patient',
        type: 'custom',
        position: { x: 50, y: 50 },
        data: { variant: 'plain', icon: <FiUsers />, label: isEnglish ? 'Patient' : 'Bệnh<br/>Nhân' },
        draggable: false,
      },
      // 3. Khối Y Tế - Top Center
      {
        id: 'node-yte',
        type: 'custom',
        position: { x: 550, y: 50 },
        data: { variant: 'hexagon', icon: <FiActivity />, label: isEnglish ? 'Healthcare<br/>Division' : 'Khối Y Tế' },
        draggable: false,
      },
      // 4. Khối Chẩn Đoán Y Khoa - Right
      {
        id: 'node-chandoan',
        type: 'custom',
        position: { x: 1050, y: 400 },
        data: { variant: 'hexagon', icon: <FiTarget />, label: isEnglish ? 'Medical<br/>Diagnostics Division' : 'Khối Chẩn<br/>Đoán Y Khoa' },
        draggable: false,
      },
      // 5. Khối IT & Digital Hub - Bottom Right
      {
        id: 'node-it',
        type: 'custom',
        position: { x: 850, y: 850 },
        data: { variant: 'hexagon', icon: <FiMonitor />, label: isEnglish ? 'IT &<br/>Digital Hub' : 'Khối IT &<br/>Digital Hub' },
        draggable: false,
      },
      // 6. Khối Dược Phẩm - Bottom Left
      {
        id: 'node-duocpham',
        type: 'custom',
        position: { x: 250, y: 850 },
        data: { variant: 'hexagon', icon: <FiPackage />, label: isEnglish ? 'Pharmaceutical<br/>Division' : 'Khối Dược<br/>Phẩm' },
        draggable: false,
      }
    ];
  }, [isEnglish]);

  const initialEdges = useMemo(() => {
    const bidirectionalEdge = {
      type: 'bezier',
      animated: true, // Enable flow animation
      style: { stroke: '#4ade80', strokeWidth: 1.5 }, // Green dashed line
      markerEnd: { type: MarkerType.ArrowClosed, color: '#4ade80' },
      markerStart: 'marker-start-green' // Using custom SVG marker
    };

    const straightBidirectionalEdge = {
      ...bidirectionalEdge,
      type: 'straight',
      animated: false,
      style: { stroke: '#3b82f6', strokeWidth: 2 }, // Solid blue line
      markerEnd: { type: MarkerType.ArrowClosed, color: '#3b82f6' },
      markerStart: 'marker-start-blue'
    };

    return [
      // 1. Patient <-> Công Nghệ Y Tế
      { id: 'e1', source: 'node-patient', target: 'node-cnyt', ...straightBidirectionalEdge, sourceHandle: 'bottom-s', targetHandle: 'top' },

      // Outer Pentagon edges (Adjusting handles to bow outwards)
      // 2. Công Nghệ Y Tế <-> Y Tế
      { id: 'e2', source: 'node-cnyt', target: 'node-yte', ...bidirectionalEdge, sourceHandle: 'top-s', targetHandle: 'left', label: isEnglish ? 'Telehealth' : 'Chăm sóc sức\nkhoẻ từ xa' },
      // 3. Y Tế <-> Chẩn Đoán
      { id: 'e3', source: 'node-yte', target: 'node-chandoan', ...bidirectionalEdge, sourceHandle: 'right-s', targetHandle: 'top', label: isEnglish ? 'Precision\nMedicine' : 'Y Học Chính\nXác' },
      // 4. Chẩn Đoán <-> IT
      { id: 'e4', source: 'node-chandoan', target: 'node-it', ...bidirectionalEdge, sourceHandle: 'bottom-s', targetHandle: 'right', label: isEnglish ? 'Digital\nDiagnostics' : 'Chẩn Đoán\nKỹ Thuật Số' },
      // 5. IT <-> Dược Phẩm
      { id: 'e5', source: 'node-it', target: 'node-duocpham', ...bidirectionalEdge, sourceHandle: 'left-s', targetHandle: 'right', label: isEnglish ? 'Data' : 'Dữ liệu' },
      // 6. Dược Phẩm <-> Công Nghệ Y Tế
      { id: 'e6', source: 'node-duocpham', target: 'node-cnyt', ...bidirectionalEdge, sourceHandle: 'left-s', targetHandle: 'bottom', label: isEnglish ? 'Drug\nDistribution System' : 'Hệ thống phân\nphối thuốc' },

      // Inner Star edges (Adjusting handles to sweep around the center and not cross exactly)
      // 7. Y Tế <-> Dược Phẩm (curve left)
      { id: 'e7', source: 'node-yte', target: 'node-duocpham', ...bidirectionalEdge, sourceHandle: 'left-s', targetHandle: 'top', label: isEnglish ? 'Treatment' : 'Trị Liệu' },
      // 8. Công Nghệ Y Tế <-> Chẩn Đoán (curve bottom/under)
      { id: 'e8', source: 'node-cnyt', target: 'node-chandoan', ...bidirectionalEdge, sourceHandle: 'bottom-s', targetHandle: 'bottom', label: isEnglish ? 'Transmission\nData' : 'Dữ Liệu Truyền\nDẫn' },
      // 9. Công Nghệ Y Tế <-> IT (curve right/down)
      { id: 'e9', source: 'node-cnyt', target: 'node-it', ...bidirectionalEdge, sourceHandle: 'right-s', targetHandle: 'left', label: isEnglish ? 'AI Technology' : 'Công nghệ AI' },
      // 10. Y Tế <-> IT (curve right)
      { id: 'e10', source: 'node-yte', target: 'node-it', ...bidirectionalEdge, sourceHandle: 'right-s', targetHandle: 'top', label: isEnglish ? 'System\nConnection' : 'Kết Nối Hệ\nThống' },
      // 11. Dược Phẩm <-> Chẩn Đoán (curve under, no label in design)
      { id: 'e11', source: 'node-duocpham', target: 'node-chandoan', ...bidirectionalEdge, sourceHandle: 'right-s', targetHandle: 'bottom' },
    ];
  }, [isEnglish]);

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  useEffect(() => {
    setNodes(initialNodes);
    setEdges(initialEdges);
  }, [initialNodes, initialEdges, setNodes, setEdges]);

  return (
    <div className="esm-interaction-page">
      <div className="layout_container">
      {/* Custom Markers for Bidirectional Edges */}
      <svg style={{ position: 'absolute', width: 0, height: 0 }}>
        <defs>
          <marker
            id="marker-start-green"
            viewBox="0 0 10 10"
            refX="4"
            refY="5"
            markerWidth="10"
            markerHeight="10"
            orient="auto-start-reverse"
          >
            <path d="M 0 1 L 8 5 L 0 9 z" fill="#4ade80" />
          </marker>
          <marker
            id="marker-start-blue"
            viewBox="0 0 10 10"
            refX="4"
            refY="5"
            markerWidth="10"
            markerHeight="10"
            orient="auto-start-reverse"
          >
            <path d="M 0 1 L 8 5 L 0 9 z" fill="#3b82f6" />
          </marker>
        </defs>
      </svg>

      <div className="flow-container">
        <div className="esm-interaction-header">
          <span>{isEnglish ? 'APPLICATION MODEL' : 'MÔ HÌNH ỨNG DỤNG'}</span>
          <h1>
            {isEnglish 
              ? 'ESM Interaction & General Medical/Pharma System' 
              : 'Sự Tương Tác ESM và Hệ Thống Y Tế Dược Tổng Quát'}
          </h1>
        </div>

        <div className="esm-interaction-flow-container">
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            nodeTypes={nodeTypes}
            fitView
            fitViewOptions={{ padding: 0.15, minZoom: 0.2, maxZoom: 1.2 }}
            nodesDraggable={true}
            zoomOnScroll={false}
            panOnScroll={true}
          >
            <Background color="#cbd5e1" gap={24} size={1.5} />
            <Controls showInteractive={false} />
          </ReactFlow>
        </div>
      </div>
    </div>
    </div>
  );
};

export default EsmInteractionFlowPage;
